// The things a Nepali utensils shop stocks, for the Stock Entry screen's
// autocomplete and size chips.
//
// Kept in step with docs/product-families.md, which records where each entry
// came from and which are guesses.
//
// `sizes` is only filled where a real standard exists -- cooker litres, thali
// inches, bucket litres. Numbered families are deliberately left empty: there is
// no standard number-to-size mapping for Nepali utensils (one maker numbers a
// 1.75 litre bhagauna 12 while another numbers a 40 litre pot below a 70 litre
// one), so seeding a guess would put wrong sizes in front of the shopkeeper.
// Those chips are learned from what the shop itself enters, which ends up being
// exactly its own wholesaler's range.

export const FAMILIES = [
  // --- steel, sized by number -------------------------------------------------
  { en: "Karai", np: "कराई", cat: "steel", sizeUnit: "no", loc: "hanging", purchaseUnit: "kg" },
  { en: "Bhagauna", np: "भगौना", cat: "steel", sizeUnit: "no" },
  { en: "Dekchi", np: "डेक्ची", cat: "steel", sizeUnit: "no" },
  { en: "Tapke", np: "तापके", cat: "steel", sizeUnit: "no" },
  { en: "Taulo", np: "ताउलो", cat: "steel", sizeUnit: "no" },
  { en: "Kasaudi", np: "कसौडी", cat: "steel", sizeUnit: "no" },
  { en: "Handi", np: "हाँडी", cat: "steel", sizeUnit: "no" },
  { en: "Tilarke", np: "तिलार्के", cat: "steel", sizeUnit: "no" },
  { en: "Dhakni", np: "ढकनी", cat: "steel", sizeUnit: "no", note: "match the karai number" },
  { en: "Lota", np: "लोटा", cat: "steel", sizeUnit: "no" },
  { en: "Amkhora", np: "अमखोरा", cat: "steel", sizeUnit: "no" },
  { en: "Gagro", np: "गाग्रो", cat: "steel", sizeUnit: "no" },
  { en: "Masala Box", np: "मसला बट्टा", cat: "steel", sizeUnit: "no" },

  // --- steel, sized by inch ---------------------------------------------------
  // purchaseUnit: the dealer charges for these by the kilo (confirmed with the
  // shop -- karai and thali only, for now). Set here so an item added without
  // ever opening "More details" still converts its next dealer bill correctly.
  { en: "Thali", np: "थाली", cat: "steel", sizeUnit: "inch", sizes: [9, 10, 11, 12, 13], gauge: true, unit: "dozen", purchaseUnit: "kg" },
  { en: "Tawa", np: "तावा", cat: "steel", sizeUnit: "inch", sizes: [8, 9, 10, 11] },
  { en: "Parat", np: "परात", cat: "steel", sizeUnit: "inch", sizes: [12, 14, 16, 18] },
  { en: "Chalni", np: "चाल्नी", cat: "steel", sizeUnit: "inch", sizes: [6, 8, 10] },
  { en: "Kachaura", np: "कचौरा", cat: "steel", sizeUnit: "inch", sizes: [4, 5, 6], unit: "dozen" },
  { en: "Glass", np: "गिलास", cat: "steel", sizeUnit: "ml", sizes: [200, 300, 400], unit: "dozen", loc: "counter" },
  { en: "Kyan", np: "क्यान", cat: "steel", sizeUnit: "litre", sizes: [1, 2, 3, 5] },

  // --- spoons and tools, mostly unsized --------------------------------------
  { en: "Chamcha", np: "चम्चा", cat: "steel", unit: "dozen", loc: "counter" },
  { en: "Kanta Chamcha", np: "काँटा चम्चा", cat: "steel", unit: "dozen", loc: "counter" },
  { en: "Daadu", np: "डाडु", cat: "steel" },
  { en: "Panyu", np: "पन्यु", cat: "steel" },
  { en: "Jhanjar", np: "झाँझर", cat: "steel" },
  { en: "Chimta", np: "चिम्टा", cat: "steel" },
  { en: "Jhir", np: "झिर", cat: "steel" },
  { en: "Chiya Channe", np: "चिया छान्ने", cat: "steel", unit: "dozen" },

  // --- brass, copper and religious -------------------------------------------
  { en: "Diyo", np: "दियो", cat: "brass", sizeUnit: "no", loc: "front_display" },
  { en: "Karuwa", np: "करुवा", cat: "brass", sizeUnit: "no" },
  { en: "Brass Lota", np: "पित्तल लोटा", cat: "brass", sizeUnit: "no" },
  { en: "Kalash", np: "कलश", cat: "brass", sizeUnit: "no", loc: "front_display" },
  { en: "Panchapatra", np: "पञ्चपात्र", cat: "brass", sizeUnit: "no" },
  { en: "Ghanti", np: "घण्टी", cat: "brass", sizeUnit: "no" },
  { en: "Sukunda", np: "सुकुन्दा", cat: "brass", sizeUnit: "no" },
  { en: "Dhupauro", np: "धुपौरो", cat: "brass" },
  { en: "Kansa Thal", np: "कास थाल", cat: "brass", sizeUnit: "inch", sizes: [8, 10, 12] },
  { en: "Copper Lota", np: "तामाको लोटा", cat: "brass", sizeUnit: "no" },
  { en: "Khadkalo", np: "खड्कलो", cat: "brass", sizeUnit: "no" },
  { en: "Fosi", np: "फोसी", cat: "brass", sizeUnit: "no" },

  // --- boxed, sized by litre --------------------------------------------------
  { en: "Pressure Cooker", np: "प्रेसर कुकर", cat: "boxed", sizeUnit: "litre", sizes: [1.5, 2, 3, 5, 7.5, 10], loc: "storage" },
  { en: "Thermos", np: "थर्मस", cat: "boxed", sizeUnit: "litre", sizes: [0.5, 1, 1.5, 2] },
  { en: "Hot Case", np: "हट केस", cat: "boxed", sizeUnit: "litre", sizes: [1, 1.5, 2, 2.5, 3] },
  { en: "Tiffin", np: "टिफिन", cat: "boxed", sizeUnit: "no", sizes: [2, 3, 4], note: "number of containers" },
  { en: "Jar", np: "जार", cat: "boxed", sizeUnit: "litre", sizes: [0.5, 1, 2, 3] },
  { en: "Kitli", np: "कित्ली", cat: "boxed", sizeUnit: "litre", sizes: [0.5, 1, 1.5, 2] },

  // --- plastic, sized by litre ------------------------------------------------
  { en: "Baltin", np: "बाल्टिन", cat: "plastic", sizeUnit: "litre", sizes: [10, 15, 20, 25], loc: "hanging" },
  { en: "Jug", np: "जग", cat: "plastic", sizeUnit: "litre", sizes: [1, 1.5, 2] },
  { en: "Mug", np: "मग", cat: "plastic", sizeUnit: "litre", sizes: [0.5, 1] },
  { en: "Drum", np: "ड्रम", cat: "plastic", sizeUnit: "litre", sizes: [30, 50, 100], loc: "storage" },
  { en: "Batta", np: "बट्टा", cat: "plastic", sizeUnit: "litre", sizes: [0.5, 1, 2, 5] },
  { en: "Tokari", np: "टोकरी", cat: "plastic" },

  // --- electric ---------------------------------------------------------------
  { en: "Electric Kettle", np: "बिजुली केतली", cat: "electric", sizeUnit: "litre", sizes: [1.5, 1.8, 2] },
  { en: "Rice Cooker", np: "राइस कुकर", cat: "electric", sizeUnit: "litre", sizes: [1.8, 2.8, 5.6] },
  { en: "Mixer Grinder", np: "मिक्सर", cat: "electric", sizeUnit: "watt", sizes: [500, 750, 1000] },
  { en: "Induction", np: "इन्डक्सन", cat: "electric", sizeUnit: "watt", sizes: [1800, 2000] },
  { en: "Iron", np: "आइरन", cat: "electric", sizeUnit: "watt", sizes: [1000, 1200] },
  { en: "Heater", np: "हिटर", cat: "electric", sizeUnit: "watt", sizes: [1000, 2000] },

  // --- cleaning ---------------------------------------------------------------
  { en: "Kucho", np: "कुचो", cat: "cleaning", loc: "hanging" },
  { en: "Mop", np: "मप", cat: "cleaning" },
  { en: "Brush", np: "ब्रस", cat: "cleaning" },
  { en: "Jali", np: "जाली", cat: "cleaning", unit: "dozen" },
  { en: "Wiper", np: "वाइपर", cat: "cleaning", sizeUnit: "inch", sizes: [12, 18] },

  // --- other ------------------------------------------------------------------
  { en: "Chakku", np: "चक्कु", cat: "other" },
  { en: "Peeler", np: "तासने", cat: "other", unit: "dozen" },
  { en: "Koreso", np: "कोर्सो", cat: "other" },
  { en: "Madani", np: "मदानी", cat: "other" },
  { en: "Chakla Belana", np: "चक्ला बेलना", cat: "other" },
  { en: "Achano", np: "अचानो", cat: "other", sizeUnit: "inch", sizes: [10, 12, 14] },
];

/**
 * The group that ties an item's sizes together.
 *
 * Derived rather than typed, so the shopkeeper never has to invent one and two
 * sizes of the same thing cannot end up in different groups over a typo.
 *
 * Shared between the bulk screen and the single-product form deliberately. They
 * used to derive it separately, which meant a 2 litre cooker added in bulk and a
 * 3 litre added on the form did not group together -- two rows in the inventory
 * list with nothing saying why. One implementation is the fix.
 */
export function variantGroupFor(name, category) {
  const base = (name || "").trim().toLowerCase();
  if (!base || !category) return null;
  return `${base.replace(/\s+/g, "-")}-${category}`;
}

/**
 * The group a new size of this item should join.
 *
 * Prefers the group its existing siblings are already in, and only invents one
 * when there are none. Deriving unconditionally is not enough: a shop's earlier
 * products may carry any group at all -- one set by hand, or seeded -- and a
 * freshly derived string would not match, leaving the new size sitting alone
 * beside the very rows it belongs with.
 */
export function groupForName(name, category, products = []) {
  const wanted = (name || "").trim().toLowerCase();
  if (!wanted || !category) return null;

  const sibling = products.find(
    (p) =>
      p.variant_group &&
      p.category === category &&
      (p.name_en || "").trim().toLowerCase() === wanted,
  );
  return sibling ? sibling.variant_group : variantGroupFor(name, category);
}

/** Families whose name starts with or contains `query`, best matches first. */
export function suggestFamilies(query, limit = 6) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return [];
  const starts = [];
  const contains = [];
  for (const f of FAMILIES) {
    const en = f.en.toLowerCase();
    if (en.startsWith(q) || f.np.startsWith(q)) starts.push(f);
    else if (en.includes(q) || f.np.includes(q)) contains.push(f);
  }
  return [...starts, ...contains].slice(0, limit);
}

/**
 * Size chips to offer for a family.
 *
 * The shop's own entries come first and are what matter -- they are its
 * wholesaler's actual range. The seeded standards fill in behind them so a
 * brand-new shop is not starting from an empty row.
 */
export function sizeChipsFor(family, products, group) {
  const seen = new Set();
  const out = [];

  const push = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || seen.has(n)) return;
    seen.add(n);
    out.push(n);
  };

  for (const p of products) {
    if (p.size_value === null || p.size_value === undefined) continue;
    const sameGroup = group && p.variant_group === group;
    const sameName = family && p.name_en?.toLowerCase() === family.en.toLowerCase();
    if (sameGroup || sameName) push(p.size_value);
  }
  (family?.sizes || []).forEach(push);

  return out.sort((a, b) => a - b);
}
